import api from '../utils/api';

/**
 * @typedef {Object} Certificate
 * @property {string} _id
 * @property {string} certificateId
 * @property {string} user userId
 * @property {string} course courseId
 * @property {string} type - 'completion', 'additional'
 * @property {string} status - 'active', 'revoked'
 * @property {string} issueDate
 * @property {string} expiryDate
 * @property {string} createdAt
 */

/**
 * @typedef {Object} CertificateRequest
 * @property {string} _id
 * @property {string} user
 * @property {string} course
 * @property {string} status - 'pending', 'approved', 'rejected'
 * @property {string} reason
 * @property {string} createdAt
 */

export const certificateService = {
  // ==================
  // PUBLIC ROUTES
  // ==================

  /**
   * Verify certificate
   * @param {string} certificateId 
   * @returns {Promise<{status: string, certificate: Certificate}>}
   */
  verifyCertificate: async (certificateId) => {
    const response = await api.post('/api/v1/certificates/verify', { certificateId });
    return response;
  },

  // ==================
  // STUDENT ROUTES
  // ==================

  /**
   * Generate primary certificate
   * @param {string} courseId 
   * @returns {Promise<{status: string, certificate: Certificate}>}
   */
  generateCertificate: async (courseId) => {
    const response = await api.post(`/api/v1/certificates/generate/${courseId}`);
    return response;
  },

  /**
   * Request additional certificate
   * @param {string} courseId 
   * @param {string} reason 
   * @returns {Promise<{status: string, request: CertificateRequest}>}
   */
  requestAdditionalCertificate: async (courseId, reason) => {
    const response = await api.post(`/api/v1/certificates/request/${courseId}`, { reason });
    return response;
  },

  /**
   * Get my certificates
   * @returns {Promise<{status: string, count: number, certificates: Certificate[]}>}
   */
  getMyCertificates: async () => {
    const response = await api.get('/api/v1/certificates/my-certificates');
    return response;
  },

  /**
   * Get my certificate requests
   * @returns {Promise<{status: string, count: number, requests: CertificateRequest[]}>}
   */
  getMyRequests: async () => {
    const response = await api.get('/api/v1/certificates/my-requests');
    return response;
  },

  // ==================
  // ADMIN ROUTES
  // ==================

  /**
   * Get all certificate requests (Admin)
   * @returns {Promise<{status: string, count: number, requests: CertificateRequest[]}>}
   */
  getAllRequests: async () => {
    const response = await api.get('/api/v1/certificates/requests');
    return response;
  },

  /**
   * Get certificate requests for a specific student (Admin)
   * @param {string} studentId 
   * @returns {Promise<{status: string, requests: Array}>}
   */
  getStudentCertificateRequests: async (studentId) => {
    // Pass studentId to filter on backend and increase limit
    const response = await api.get(`/api/v1/certificates/requests?student=${studentId}&limit=100`);
    
    return {
      status: response.status,
      requests: response.data.requests
    };
  },

  /**
   * Approve certificate request (Admin)
   * @param {string} requestId 
   * @returns {Promise<{status: string, message: string, certificate: Certificate}>}
   */
  approveRequest: async (requestId) => {
    const response = await api.patch(`/api/v1/certificates/requests/${requestId}/approve`);
    return response;
  },

  /**
   * Reject certificate request (Admin)
   * @param {string} requestId 
   * @param {string} rejectionReason 
   * @returns {Promise<{status: string, message: string, request: CertificateRequest}>}
   */
  rejectRequest: async (requestId, adminNote) => {
    const response = await api.patch(`/api/v1/certificates/requests/${requestId}/reject`, { adminNote });
    return response;
  },

  /**
   * Revoke certificate (Admin)
   * @param {string} certificateId 
   * @param {string} reason 
   * @returns {Promise<{status: string, message: string}>}
   */
  revokeCertificate: async (certificateId, reason) => {
    const response = await api.patch(`/api/v1/certificates/${certificateId}/revoke`, { reason });
    return response;
  },
};
